import {ListColumnModel} from './list-column.model';
import {PaginationModel} from './pagination.model';
import {format} from 'date-fns';

export class RequestParamModel {
  pageIndex = 0;
  pageSize?: any;
  asc = true;
  fields!: string[];
  expand!: string[];
  expands!: string[];
  totalCount!: number;
  totalPage!: number;
  filter?: string;
  defaultFilter = '';
  setOrArrayFilter = '';
  sorts?: string;
  group?: any;
  storeRecentProduct?: boolean;
  pageCount?: any;

  filters!: any[]

  /**
   * Constructor for initializing the pagination settings.
   *
   * @return {Object} Returns an instance of the object with default pagination values including `pageIndex` set to 0 and `pageSize` set to 20.
   */
  constructor() {
    this.pageIndex = 0;
    this.pageSize = 20;
  }

  /**
   * Updates the pagination attributes based on the provided PaginationModel.
   *
   * @param {PaginationModel<T>} pagedList - The pagination model containing the new values to update.
   * @return {void}
   */
  update<T>(pagedList: PaginationModel<T>) {
    this.pageSize = pagedList.pageSize;
    this.totalCount = pagedList.totalCount;
    this.pageIndex = pagedList.currentPage;
    this.totalPage = pagedList.pageCount;
  }

  /**
   * Generates a filter string based on the provided columns and their search values.
   *
   * @param {ListColumnModel[]} columns - The array of column models containing search parameters.
   * @return {any} The generated filter string.
   */
  setFilters(columns: ListColumnModel[]): any {
    this.filter = '';
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].searchable && columns[i].searchValue) {
        const field = columns[i].searchfield;
        const searchValue = encodeURI(columns[i].searchValue);
        if (columns[i].datatype === 'date') {
          this.filter += '&filter[' + field + ']' + '=' + format(new Date(columns[i].searchValue), 'yyyy-MM-dd');
        } else if (columns[i].datatype !== 'number') {
          this.filter += '&filter[' + field + '][like]' + '=' + searchValue;
        } else {
          this.filter += '&filter[' + field + ']' + '=' + searchValue;
        }
      }
    }
  }

  /**
   * Adds a default filter to the existing filter query string based on the specified field, value, and datatype.
   *
   * @param {string} field - The field name to apply the filter on.
   * @param {any} value - The value to filter the field by.
   * @param {string} datatype - The datatype of the field, used to determine the type of filter to apply.
   * @return {void}
   */
  addDefaultFilter(field: string, value: any, datatype: string) {
    if (field && value) {
      if (datatype !== 'number') {
        this.defaultFilter += '&filter[' + field + ']' + '[like]' + '=' + value;
      } else {
        this.defaultFilter += '&filter[' + field + ']' + '=' + value;
      }
    }
  }

  addDefaultFilters(field: string, value: any, datatype: string) {
    if (field && (value || typeof value === "boolean" && !value)) {
      if (datatype == 'date') {
        this.defaultFilter += '&filters[' + field + ']' + '[date]' + '=' + value;
      } else if (datatype !== 'number') {
        this.defaultFilter += '&filters[' + field + ']' + '[like]' + '=' + value;
      } else {
        this.defaultFilter += '&filters[' + field + ']' + '=' + value;
      }
    }
  }

  /**
   * Filters the provided array and appends query string parameters.
   *
   * @param {Array} arr - The array to filter and encode.
   * @param {string} field - The field name used for query parameter construction.
   * @return {void} No return value.
   */
  filterAndWithArray(arr: any, field: any) {
    if (arr !== undefined) {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] !== '') {
          this.setOrArrayFilter += '&assignedto[' + field + '][' + i + ']' + '=' + encodeURIComponent(arr[i]);
        }
      }
    }
  }

  /**
   * Adds filter parameters to the query string for the specified field using the values in the given array.
   *
   * @param {Array} arr - The array containing the values to be used in the filter.
   * @param {string} field - The field name to which the filter should be applied.
   * @return {void}
   */
  filterInWithArray(arr: any, field: any) {
    if (arr !== undefined) {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] !== '') {
          this.setOrArrayFilter += '&filter[' + field + '][in][]' + '=' + encodeURIComponent(arr[i]);
        }
      }
    }
  }

  /**
   * Applies a minimum and optional maximum filter to the specified field and updates the default filter string.
   *
   * @param {string} field - The field to which the filter should be applied.
   * @param {number} minValue - The minimum value for the filter.
   * @param {number} [maxValue] - The optional maximum value for the filter.
   * @return {void}
   */
  filterMinMax(field: any, minValue: any, maxValue?: any) {
    if (field) {
      if (minValue) {
        this.defaultFilter += '&filter[' + field + '][min]' + '=' + minValue;
      }
      if (maxValue) {
        this.defaultFilter += '&filter[' + field + '][max]' + '=' + maxValue;
      }
    }
  }

  /**
   * Filters out elements in the provided array that are not empty strings and constructs a filter query string.
   *
   * @param {Array} arr - Array of values to be filtered and used in the query string.
   * @param {string} field - The field name to be used in the filter query string.
   * @return {void} This function does not return a value.
   */
  filterNotInWithArray(arr: any, field: any) {
    if (arr !== undefined) {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] !== '') {
          this.setOrArrayFilter += '&filter[' + field + '][nin][' + i + ']' + '=' + encodeURIComponent(arr[i]);
        }
      }
    }
  }

  /**
   * Sets the default filter for a dropdown field.
   *
   * @param {string} field - The name of the field to be filtered.
   * @param {any} value - The value to filter the field by.
   * @param {string} datatype - The datatype of the field (e.g., 'number', 'date').
   * @return {void}
   */
  setDefaultFiltersDropdown(field: string, value: any, datatype: string) {
    if (field && value) {
      if (datatype !== 'number') {

        if (datatype === 'date') {
          this.defaultFilter += '&filter[dropdown][' + field + ']' + '=' + format(new Date(value), 'yyyy-MM-dd');
        } else {
          this.defaultFilter += '&filter[dropdown][' + field + ']' + '[like]' + '=' + encodeURIComponent(value);
        }
      } else {
        this.defaultFilter += '&filter[dropdown][' + field + ']' + '=' + encodeURIComponent(value);
      }
    }
  }

  /**
   * Adds a filter to the default filter string for assigning tasks to a specified field and value.
   *
   * @param {string} field - The field to which the task is assigned.
   * @param {any} value - The value to be assigned to the field. If not provided, it defaults to null.
   * @return {void}
   */
  addAssignedToFilter(field: string, value: any) {
    if (field && value) {
      this.defaultFilter += '&assignedto[' + field + ']' + '=' + encodeURIComponent(value);
    } else if (field) {
      this.defaultFilter += '&assignedto[' + field + ']' + '=' + null;
    }
  }

  /**
   * Resets the filter and clears the search values for all columns in the provided list.
   *
   * @param {ListColumnModel[]} columns - The list of columns to reset.
   * @return {void}
   */
  resetClear(columns: ListColumnModel[]) {
    this.filter = '';
    for (let i = 0; i < columns.length; i++) {
      columns[i].searchValue = '';
    }

  }

  /**
   * Sets the sorting order for the provided columns.
   *
   * @param {ListColumnModel[]} columns - An array of columns with sorting orders and properties.
   * @return {void} No return value.
   */
  setSorting(columns: ListColumnModel[]): any {
    this.sorts = '';
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].sortOrder && columns[i].sortOrder != '') {
        this.sorts += (this.sorts != '' ? ',' : '') + (columns[i].sortOrder === 'desc' ? '-' : '') + columns[i].property
      }
    }
  }

  /**
   * Resets the sorting order of the given list of columns by clearing any
   * existing sort orders.
   *
   * @param {ListColumnModel[]} columns - An array of column models to reset.
   * @return {void} This method does not return a value.
   */
  resetSorting(columns: ListColumnModel[]) {
    this.sorts = '';
    for (let i = 0; i < columns.length; i++) {
      columns[i].sortOrder = '';
    }

  }

  /**
   * Sorts the data based on the specified field and sort type.
   *
   * @param {string} field - The field by which to sort the data.
   * @param {string} sortType - The type of sort to apply ('asc' or 'desc').
   * @return {any} The sorted data.
   */
  sortChange(field: string, sortType: string): any {
    if (field) {
      this.sorts = (sortType === 'desc' ? '-' : '') + field
    }
  }

  /**
   * Constructs a query parameter string from various filter and sorting options.
   *
   * @return {string} A string representing the query parameters for a request.
   */
  getParams(): string {
    // TODO : Optimize to use the URLQueryParams
    let param = '';

    if (this.defaultFilter) {
      param += this.defaultFilter;
    }

    if (this.filter) {
      param += this.filter;
    }
    const fieldParam = this.getListParam('fields', this.fields);
    if (fieldParam) {
      param += '&' + fieldParam;
    }

    const expandParam = this.getListParam('expand', this.expand);
    if (expandParam) {
      param += '&' + expandParam;
    }

    const expandsParam = this.getListParam('expands', this.expands);
    if (expandsParam) {
      param += '&' + expandsParam;
    }

    if (this.sorts) {
      param += '&sort=' + (!this.asc ? '-' : '') + this.sorts;
    }

    if (this.pageIndex) {
      param += '&page=' + this.pageIndex;
    }
    if (this.pageSize) {
      param += '&pageSize=' + this.pageSize;
    }
    if (this.group) {
      param += '&group=' + this.group;
    }
    if (this.storeRecentProduct) {
      param += '&storeRecentProduct=' + this.storeRecentProduct;
    }
    if (param) {
      param = param.replace('&', '?');
    }
    if (this.setOrArrayFilter) {
      param += this.setOrArrayFilter;
    }
    return param;
  }

  /**
   * Constructs a query parameter for a list of values.
   *
   * @param paramName The name of the query parameter.
   * @param paramValue An array of values to be included in the query parameter.
   * @return A string representing the constructed query parameter.
   */
  private getListParam(paramName: string, paramValue: string[]): string {
    let param = '';
    if (paramValue) {
      param = paramName + '=' + paramValue.join(',');
    }
    return param;
  }


}
